import * as React from 'react';
import { cn } from '@/lib/utils';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';

type Tag = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'div';

interface SplitTextProps extends React.HTMLAttributes<HTMLElement> {
  children: string;
  as?: Tag;
  className?: string;
  /** Delai inter-char en ms. Default 35ms cinematic sans fatigue. */
  staggerMs?: number;
  /** Delai global avant le debut du stagger. Default 0. */
  startDelay?: number;
}

/**
 * SplitText  H1/H2 cinematic letter-by-letter entry (Awwwards editorial pattern).
 *
 * Phase 27.3 :
 * - Split children string en WORDS (whitespace-nowrap inline-block), puis chaque
 *   word contient ses CHARS individuels en inline-block. Cette nesting empeche
 *   les coupures mid-word ("protection" -> "p" + "rotection") tout en gardant
 *   l'animation char-by-char. Browser wrap aux espaces (text nodes) entre mots.
 * - aria-label sur le Tag (accessible name lu par AT). Chars + word wrappers
 *   sont aria-hidden pour eviter duplication de texte cote crawlers SEO.
 * - Stagger transition-delay calcule inline (35ms par char, continu cross-words).
 * - Initial state per char : opacity 0 + translateY(0.6rem) + blur(8px)
 * - Reveal state : opacity 1 + translateY(0) + blur(0), 700ms ease-out-expo.
 * - Trigger via useScrollReveal (IntersectionObserver fires once).
 * - Reserve aux strings simples. Pour mixed content, use ScrollReveal classique.
 *
 * Usage :
 *   <SplitText as="h2" className="font-display text-5xl">Et si demain...</SplitText>
 */
export function SplitText({
  children,
  as = 'h2',
  className,
  staggerMs = 35,
  startDelay = 0,
  ...rest
}: SplitTextProps) {
  const { ref, visible } = useScrollReveal<HTMLElement>();
  const Tag = as as React.ElementType;

  // Split en mots (preserve les espaces multiples comme separateurs).
  const words = children.split(' ');

  // Index char global pour stagger continu cross-words.
  let charIndex = 0;

  return (
    <Tag
      ref={ref as React.Ref<HTMLElement>}
      aria-label={children}
      className={cn('split-text', visible && 'split-text-revealed', className)}
      {...rest}
    >
      {words.map((word, wordIdx) => {
        const wordChars = Array.from(word);
        return (
          <React.Fragment key={wordIdx}>
            {/* Word wrapper inline-block + whitespace-nowrap : empeche les chars
                de se separer en wrap. Le browser ne peut couper qu'entre words
                (au niveau des espaces text nodes ci-dessous). */}
            <span aria-hidden="true" className="inline-block whitespace-nowrap">
              {wordChars.map((char) => {
                const delay = startDelay + charIndex * staggerMs;
                charIndex += 1;
                return (
                  <span
                    key={charIndex}
                    className="split-text-char inline-block"
                    style={{
                      transitionDelay: visible ? `${delay}ms` : '0ms'
                    }}
                  >
                    {char}
                  </span>
                );
              })}
            </span>
            {/* Espace inter-word en text node : permet le line wrap entre mots. */}
            {wordIdx < words.length - 1 && ' '}
          </React.Fragment>
        );
      })}
    </Tag>
  );
}
